import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import LogoMarkSvg from '../assets/logos/logo-mark.svg?react';
import { MyPageSettingsPageLayout } from '../features/my-page-settings/ui/my-page-settings-page-layout';
import {
  MyPageSettingsPinScreen,
  type MyPageSettingsPinPhase,
} from '../features/my-page-settings/ui/my-page-settings-pin-screen';
import { useEmployeeSession } from '../shared/hooks/use-employee-session';
import {
  mainHeadingTypographyStyles,
  ViewportFrame,
  ViewportPage,
} from '../shared/ui';

const ForcedPinContent = styled.div.attrs({
  'data-ui': 'forced-pin-content',
})`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ForcedPinBrand = styled.div.attrs({
  'data-ui': 'forced-pin-brand',
})`
  display: grid;
  align-content: start;
  gap: ${({ theme }) => theme.scale.px(3)};
`;

const ForcedPinLogo = styled(LogoMarkSvg)`
  width: ${({ theme }) => theme.scale.px(108)};
  height: ${({ theme }) => theme.scale.px(31)};
  display: block;
  color: ${({ theme }) => theme.colors.text.primary};
  --logo-mark-accent: ${({ theme }) => theme.colors.brand.primary};
`;

const ForcedPinTagline = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(8)};
  font-weight: 600;
  line-height: 1.18;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ForcedPinTitle = styled.h1`
  ${mainHeadingTypographyStyles};
  width: ${({ theme }) => theme.scale.px(320)};
  margin-top: ${({ theme }) => theme.scale.px(43)};
`;

const ForcedPinFormSlot = styled.div`
  width: 100%;
  margin-top: ${({ theme }) => theme.scale.px(81)};
`;

export function MyPagePinPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    changeSessionPin,
    hasPinProtection,
    saveSessionPin,
    verifySessionPin,
  } = useEmployeeSession();
  const isCreatingPin = !hasPinProtection;
  const [forcedPinPhase, setForcedPinPhase] =
    useState<MyPageSettingsPinPhase>('new');
  const forcedPinTitleKey =
    forcedPinPhase === 'confirm'
      ? 'myPageSettings.pin.confirmTitle'
      : 'myPageSettings.pin.createTitle';
  const handleSubmitPin = async ({
    currentPin,
    nextPin,
  }: {
    currentPin: string | null;
    nextPin: string;
  }) => {
    if (currentPin === null) {
      await saveSessionPin(nextPin);
      return true;
    }

    return changeSessionPin(currentPin, nextPin);
  };
  const handleComplete = () => {
    navigate(isCreatingPin ? '/' : '/settings/security', {
      replace: isCreatingPin,
    });
  };

  if (isCreatingPin) {
    return (
      <ViewportPage dataUi="forced-pin-page">
        <ViewportFrame
          dataUi="forced-pin-frame"
          paddingBottomPx={28}
          paddingLeftPx={31}
          paddingRightPx={30}
          paddingTopPx={25}
        >
          <ForcedPinContent>
            <ForcedPinBrand>
              <ForcedPinLogo role="img" aria-label="grato" />
              <ForcedPinTagline>
                {t('homeDashboard.brandTagline')}
              </ForcedPinTagline>
            </ForcedPinBrand>

            <ForcedPinTitle>{t(forcedPinTitleKey)}</ForcedPinTitle>

            <ForcedPinFormSlot>
              <MyPageSettingsPinScreen
                autoFocus
                hasPinProtection={hasPinProtection}
                onPhaseChange={setForcedPinPhase}
                onSubmit={handleSubmitPin}
                onComplete={handleComplete}
                showPrompt={false}
              />
            </ForcedPinFormSlot>
          </ForcedPinContent>
        </ViewportFrame>
      </ViewportPage>
    );
  }

  return (
    <MyPageSettingsPageLayout
      backAriaLabel={t('myPage.backAria')}
      frameDataUi="settings-pin-frame"
      onBack={() => {
        navigate('/settings/security');
      }}
      onOpenHome={() => {
        navigate('/home');
      }}
      onOpenMyPage={() => {
        navigate('/my-page');
      }}
      pageDataUi="settings-pin"
      title={t('myPageSettings.pin.title')}
    >
      <MyPageSettingsPinScreen
        hasPinProtection={hasPinProtection}
        onValidateCurrentPin={verifySessionPin}
        onSubmit={handleSubmitPin}
        onComplete={handleComplete}
      />
    </MyPageSettingsPageLayout>
  );
}
